import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  TableState,
  useReactTable,
  type SortingState
} from '@tanstack/react-table';
import Header from './includes/Header';
import MainTable from './includes/MainTable';
import { type DataTableProps } from './types';
import { useMemo, useState } from 'react';
import Footer from './includes/Footer';
import DownloadDataDropdown from './includes/DownloadDataDropdown';
import useGetBasePath from '@/hooks/useGetBasePath';
import { cn } from '@/lib/utils/global';

function DataTable<T>({
  data,
  columns,
  showFilter,
  filterBy,
  search,
  selectComponent: SelectComponent,
  hasInternalPagination,
  expandableComponent,
  modifiedColumnNames,
  expansionDepth,
  onSelectSubmit,
  columnOrder,
  columnPinning,
  isDownloadable,
  needsVirtualization,
  idForDownloadDropdown,
  obisCodeMap,
  clipBoardCols,
  title,
  onRowClicked
}: DataTableProps<T>) {
  filterBy = filterBy || ['name'];

  const basePath = useGetBasePath(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const showSelectedInfo = SelectComponent !== undefined;

  const selectedItems = useMemo(() => {
    return Object.keys(rowSelection).map((dataIndex) => {
      const dataItem = data.at(Number(dataIndex)) as T;
      return dataItem;
    });
  }, [data, rowSelection]);

  const tableState: Partial<TableState> = {
    sorting,
    rowSelection,
    columnOrder: columnOrder || [],
    columnPinning: columnPinning || {}
  };

  if (!hasInternalPagination) {
    tableState['pagination'] = {
      pageIndex: 0,
      pageSize: data.length
    };
  }

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    getRowCanExpand: () => (expandableComponent ? true : false),
    state: tableState
  });

  return (
    <>
      <div className="flex justify-end items-center gap-3 mb-2">
        {table && (
          <Header
            search={search}
            dataLength={data.length}
            showFilter={showFilter}
            filterBy={filterBy}
            table={table}
          />
        )}

        {isDownloadable && data?.length > 0 && (
          <DownloadDataDropdown
            id={idForDownloadDropdown || null}
            data={data}
            title={title}
          />
        )}
      </div>

      {showSelectedInfo && (
        <div className="relative h-[50px] w-full">
          <SelectComponent
            selected={selectedItems}
            onSelectSubmit={onSelectSubmit}
          />
        </div>
      )}

      <div
        className={cn(
          'overflow-auto flex-1 relative scrollable-content rounded-sm',
          basePath !== 'mdms' && 'border'
        )}>
        <MainTable
          expansionDepth={expansionDepth}
          clipBoardCols={clipBoardCols}
          onRowClicked={onRowClicked}
          needsVirtualization={needsVirtualization}
          modifiedColumnNames={modifiedColumnNames}
          obisCodeMap={obisCodeMap}
          expandableComponent={expandableComponent}
          table={table}
          columns={columns}
        />
      </div>

      {hasInternalPagination && data.length > 10 && <Footer table={table} />}
    </>
  );
}

export default DataTable;
